import { format } from 'date-fns';
import _ from 'lodash';
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { BaseSelect } from '../../base/select';
import { DatePicker } from '../../base/datepicker';
import * as S from './message-fields.styles';
import { CoreFetch } from '../../../api';
import { UserApi, TagApi, MessageApi } from '../../../api/server';
import { MessagingContext } from '../../../services';

export interface IMessageFieldsValues {
    priority?: string;
    dueDate?: string; // Format: yyyy-MM-dd
    categoryIds?: string[];
    assignedUserIds?: string[];
}

interface IMessageFieldsProps {
    values: IMessageFieldsValues;
    onChange: (values: IMessageFieldsValues) => void;
}

export const MessageFields: React.FunctionComponent<IMessageFieldsProps> = (props: IMessageFieldsProps): React.ReactElement => {
    const { values, onChange } = props;
    const context = useContext(MessagingContext);
    const isSupportApp = context.account?.supportGrant === true;
    
    // Check if user has CODING_ACCESS role
    const currentAccount = context.account?.accountAccess?.find(
        acc => acc.accountId === context.account?.grantedAccountId
    );
    const isCodingAccessOnly = currentAccount?.roleId === 'CODING_ACCESS';
    
    const [categoryOptions, setCategoryOptions] = useState<Array<{ value: string; label: string }>>([]);
    const [userOptions, setUserOptions] = useState<Array<{ value: string; label: string }>>([]);

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const categories = isSupportApp
                    ? await CoreFetch(MessageApi).listSupportMessageCategory()
                    : await CoreFetch(MessageApi).listProviderMessageCategory();
                
                const categoryOpts = _.map(categories, category => ({
                    value: category.id || '',
                    label: category.name || ''
                }));
                
                setCategoryOptions(categoryOpts);
            } catch (error) {
                console.error('Failed to load message categories:', error);
            }

            // Skip user query if user has CODING_ACCESS role
            if (!isCodingAccessOnly) {
                try {
                    const users = await CoreFetch(UserApi).listProviderUsers({});
                    
                    const userOpts = _.map(users, user => ({
                        value: user.personalAccountId || '',
                        label: `${user.first || ''} ${user.last || ''}`.trim() || user.email || 'Unknown User'
                    }));
                    setUserOptions(userOpts);
                } catch (error) {
                    console.error('Failed to load users:', error);
                }
            }
        };

        fetchOptions();
    }, [isSupportApp, isCodingAccessOnly]);

    const handlePriorityChange = (option: any) => {
        const newValue = option?.value;
        onChange({ ...values, priority: newValue });
    };

    const handleDueDateChange = useCallback((date: Date | undefined) => {
        if (!date) {
            if (values.dueDate !== undefined) {
                onChange({ ...values, dueDate: undefined });
            }
            return;
        }
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;
        if (values.dueDate !== dateString) {
            onChange({ ...values, dueDate: dateString });
        }
    }, [values.dueDate, onChange]);

    const handleCategoryChange = (options: any) => {
        const newValues = options ? _.map(options, 'value') : [];
        onChange({ ...values, categoryIds: newValues });
    };

    const handleAssignedUserChange = (options: any) => {
        const newValues = options ? _.map(options, 'value') : [];
        onChange({ ...values, assignedUserIds: newValues });
    };

    return (
        <>
            <S.FieldRow>
                <S.FieldColumn>
                    <S.FieldLabel>Priority</S.FieldLabel>
                    <BaseSelect
                        options={[
                            { value: 'LOW', label: 'Low' },
                            { value: 'NORMAL', label: 'Normal' },
                            { value: 'HIGH', label: 'High' },
                            { value: 'URGENT', label: 'Urgent' }
                        ]}
                        value={values.priority ? { value: values.priority, label: _.capitalize(values.priority) } : null}
                        onChange={handlePriorityChange}
                        placeholder="Select Priority"
                        disabled={false}
                        isClearable={true}
                    />
                </S.FieldColumn>
                <S.FieldColumn>
                    <S.FieldLabel>Due Date</S.FieldLabel>
                    <DatePicker
                        onDateSelected={handleDueDateChange}
                        value={values.dueDate ? values.dueDate : undefined}
                        inputProps={{
                            placeholder: 'Choose date',
                        }}
                    />
                </S.FieldColumn>
            </S.FieldRow>
            <S.FieldRow>
                <S.FieldColumn>
                    <S.FieldLabel>Category</S.FieldLabel>
                    <BaseSelect
                        isMulti={true}
                        options={categoryOptions}
                        value={categoryOptions.filter(opt => values.categoryIds?.includes(opt.value))}
                        onChange={handleCategoryChange}
                        placeholder="Select Categories"
                        disabled={false}
                        isClearable={true}
                    />
                </S.FieldColumn>
                <S.FieldColumn>
                    <S.FieldLabel>Assignee</S.FieldLabel>
                    <BaseSelect
                        isMulti={true}
                        options={userOptions}
                        value={userOptions.filter(opt => values.assignedUserIds?.includes(opt.value))}
                        onChange={handleAssignedUserChange}
                        placeholder="Select Users"
                        disabled={isCodingAccessOnly}
                        isClearable={true}
                    />
                </S.FieldColumn>
            </S.FieldRow>
        </>
    );
};

